import { useCallback, useRef, useEffect } from 'react';
import { log } from 'log';
import { pick } from 'ramda';
import { useSocketSelector } from 'js/store/hooks';
import { type SocketIOClientSocket } from 'socket.io-client';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

import * as helpers from './helpers';
import * as constants from './constants';
import { removeStackEntry, addStackEntry } from '../../helpers';
import { IServerSendResponse, TServerSendRequest, IDeferredStackEntry } from '../../types';

export const useGenericSend = () => {
    const { connection, connected, isError } = useSocketSelector(pick(['connection', 'connected', 'isError']));
    const deferredStack = useRef<IDeferredStackEntry[]>([]);
    const socketStateRef = useRef<{
        connection?: SocketIOClientSocket;
        connected: boolean;
        isError: boolean;
    } | null>(null);

    const send = useCallback(<RequestData, ResponseData>(request: TServerSendRequest<RequestData>) => {
        return new Promise<IServerSendResponse<ResponseData>>((resolve, reject) => {
            const timestamp = Date.now();

            if (socketStateRef.current?.isError || !socketStateRef.current?.connection) {
                reject({ reason: 'No connection', request });

                return;
            }

            const updatedRequest = helpers.updateRequest(request);

            if (request.ID === constants.BATCH_REQUEST_ID && request.requests) {
                const promises: Promise<void>[] = [];

                for (const subRequest of request.requests.map(helpers.updateCorrelationId)) {
                    let subResolve: any;
                    let subReject: any;

                    promises.push(
                        new Promise((localResolve, localReject) => {
                            subResolve = localResolve;
                            subReject = localReject;
                        })
                    );

                    const stackEntry = {
                        request: subRequest,
                        resolve: subResolve,
                        reject: subReject,
                        timestamp,
                        timer: setTimeout(() => {
                            removeStackEntry(stackEntry);

                            reject({
                                reason: 'Request timed out',
                                request: subRequest
                            });
                        }, constants.REQUEST_TIMEOUT)
                    };
                }

                const stackEntry = {
                    request: updatedRequest,
                    resolve,
                    reject,
                    timestamp,
                    timer: setTimeout(() => {
                        removeStackEntry(stackEntry);

                        reject({
                            reason: 'Request timed out',
                            request: updatedRequest
                        });
                    }, constants.REQUEST_TIMEOUT)
                };

                addStackEntry(stackEntry);
                socketStateRef.current?.connection.send(JSON.stringify(updatedRequest));

                return Promise.all(promises);
            }

            if (request.responses) {
                const stackEntry = {
                    request: updatedRequest,
                    resolve,
                    reject,
                    timestamp,
                    timer: setTimeout(() => {
                        removeStackEntry(stackEntry);

                        reject({
                            reason: 'Request timed out',
                            request: updatedRequest
                        });
                    }, constants.REQUEST_TIMEOUT)
                };

                addStackEntry(stackEntry);
            }

            socketStateRef.current?.connection.send(JSON.stringify(updatedRequest));
        }).catch((error) => {
            publicEventsAPIService
                .trigger('socket-breakdown', {
                    reason: error.reason,
                    requestId: error.request.ID
                });

            log('oapiError', error);

            throw error;
        });
    }, []);

    useEffect(() => {
        socketStateRef.current = { connection, connected, isError };
    }, [connection, connected, isError]);

    useEffect(() => {
        if ((connected || isError) && deferredStack.current.length) {
            for (const deferredEntry of deferredStack.current) {
                const { request, resolve, reject } = deferredEntry;

                send(request)
                    .then(resolve)
                    .catch(reject);

                deferredStack.current = deferredStack.current.filter(entry => entry !== deferredEntry);
            }
        }
    }, [send, connected, isError]);

    return useCallback(
        <RequestData = object, ResponseData = object>(
            request: TServerSendRequest<RequestData>
        ): Promise<IServerSendResponse<ResponseData>> => {
            log('oapiRequest', request);

            if (socketStateRef.current?.isError || socketStateRef.current?.connected) {
                return send(request);
            }

            // fulfill the promise when the connection be established or failed
            return new Promise((resolve, reject) => {
                deferredStack.current.push({ request, resolve, reject });
            });
        },
        [send]
    );
};
